import type { CountryCodes, Locales } from '@/enums/general'
import type { RouteLocationNormalized } from 'vue-router'
import { logError } from '@/utils/general'
import { datadogRum } from '@datadog/browser-rum'

export const parseLanguage = (language?: string): string => language?.split('-')[0]?.toLowerCase() ?? ''

export const parseCountry = (language?: string): string => language?.split('-')[1]?.toLowerCase() ?? ''

interface IsValidLocaleCountryParamParams {
  appStore: ReturnType<typeof useAppStore>
  localeCountryParam: string | undefined
}

/**
 * Checks if the locale-country param is valid
 *
 * @param params - Object containing appStore and localeCountryParam
 * @returns boolean indicating if the locale-country param is valid
 */
export function isValidLocaleCountryParam({ appStore, localeCountryParam }: IsValidLocaleCountryParamParams): boolean {
  if (!localeCountryParam) return false

  const locale = localeCountryParam.split('-')[0]
  const country = localeCountryParam.split('-')[1]

  return (
    appStore.locales.includes(locale as (typeof Locales)[keyof typeof Locales]) &&
    appStore.countries.includes(country as (typeof CountryCodes)[keyof typeof CountryCodes])
  )
}

interface LocaleCountryData {
  locale: string
  country: string
}

interface DetermineLocaleCountryPrefixParams {
  route: RouteLocationNormalized | { path: string; fullPath: string }
  appStore: ReturnType<typeof useAppStore>
  authStore: ReturnType<typeof useAuthStore>
  cookieLocale?: string | null
  cookieCountry?: string | null
  acceptLanguageHeader?: string
}

interface GetBrowserValuesParams {
  appStore: ReturnType<typeof useAppStore>
  defaults: LocaleCountryData
  browserLocales: readonly string[] | string[]
}

/**
 * Checks if only the country param is filled (e.g., /fr, /gb)
 *
 * @param params - Object containing appStore and localeCountryParam
 * @returns boolean indicating if only valid country param is present
 */
export function hasOnlyValidCountryParam({
  appStore,
  localeCountryParam
}: {
  appStore: ReturnType<typeof useAppStore>
  localeCountryParam: string | undefined
}): boolean {
  const onlyContainsCountryParam = !localeCountryParam?.includes('-')
  const country = localeCountryParam || ''

  return onlyContainsCountryParam && appStore.countries.includes(country as (typeof CountryCodes)[keyof typeof CountryCodes])
}

/**
 * Detects locale and country from browser values (Accept-Language header, and geo lookup on the client)
 *
 * @param params - Object containing appStore, defaults and browserLocales
 * @returns LocaleCountryData object with detected locale and country
 */
async function getBrowserValues({ appStore, defaults, browserLocales }: GetBrowserValuesParams): Promise<LocaleCountryData> {
  let locale = defaults.locale
  let country = defaults.country

  // Get browser language
  browserLocales.some((value) => {
    const newLocale = value.split('-')[0]

    if (!appStore.locales.includes(newLocale as (typeof Locales)[keyof typeof Locales])) {
      return false
    }

    locale = newLocale || ''
    return true
  })

  // Serverside geo lookup is handled by a CloudFront function
  if (import.meta.client) {
    // Temporarily log the usage of GeoJS
    datadogRum.addAction('geojs-country-lookup')

    try {
      const response = await fetch('https://get.geojs.io/v1/ip/country.json')
      const { country: geoCountry } = (await response.json()) as { country: string }

      const lowerCaseGeoCountry = geoCountry.toLowerCase()

      if (appStore.countries.includes(lowerCaseGeoCountry as (typeof CountryCodes)[keyof typeof CountryCodes])) {
        country = lowerCaseGeoCountry
      }
    } catch (e) {
      logError(e)
    }
  }

  return {
    locale,
    country
  }
}

/**
 * Gets the locale and country using browser detection
 *
 * @param params - Object containing appStore, authStore, defaults and browserLocales
 * @returns LocaleCountryData object with locale and country
 */
async function getData({
  appStore,
  authStore,
  defaults,
  browserLocales
}: {
  appStore: ReturnType<typeof useAppStore>
  authStore: ReturnType<typeof useAuthStore>
  defaults: LocaleCountryData
  browserLocales: readonly string[] | string[]
}): Promise<LocaleCountryData> {
  const data: LocaleCountryData = await getBrowserValues({ appStore, defaults, browserLocales })

  // Override country for authenticated users
  if (authStore.isAuthenticated) {
    data.country = authStore.user?.user?.country?.name?.toLowerCase() || defaults.country
  }

  return data
}

/**
 * Determines the locale-country prefix that should be used for a given route.
 * This function implements the same logic as the i18n middleware but returns
 * the prefix string instead of performing redirects.
 *
 * @param params - Object containing route, stores, cookies, and optional headers
 * @returns Promise resolving to the locale-country prefix string (e.g., "nl-nl")
 *
 * @example
 * ```ts
 * import { determineLocaleCountryPrefix } from '@/utils/parse'
 * import { CLOCALE, CCOUNTRY } from '@/utils/cookie/cookieTypes'
 *
 * const prefix = await determineLocaleCountryPrefix({
 *   route: useRoute(),
 *   appStore: useAppStore(),
 *   authStore: useAuthStore(),
 *   cookieLocale: useCookie(CLOCALE).value,
 *   cookieCountry: useCookie(CCOUNTRY).value
 * })
 * // Returns: "nl-nl"
 * ```
 */
export async function determineLocaleCountryPrefix({
  route,
  appStore,
  authStore,
  cookieLocale,
  cookieCountry,
  acceptLanguageHeader
}: DetermineLocaleCountryPrefixParams): Promise<string> {
  // Step 1: Extract and prepare initial data
  const pathSegments = route.path.split('/').filter(Boolean)
  const localeCountryParam = pathSegments[0] // First segment should be locale-country

  // Build defaults object
  const defaults: LocaleCountryData = {
    locale: (cookieLocale as string) || 'en',
    country: authStore.loggedIn
      ? authStore.user?.user?.country?.name?.toLowerCase() ?? appStore.defaultCountry
      : (cookieCountry as string) || appStore.defaultCountry
  }

  // Step 2: Handle no prefix case (root or path without locale-country)
  if (!localeCountryParam) {
    if (cookieLocale && cookieCountry) {
      // We have cookies - return cookie-based locale-country
      return `${cookieLocale}-${cookieCountry}`
    } else {
      // No cookies - determine locale/country using browser detection
      let browserLocales: readonly string[] | string[] = []

      if (import.meta.server) {
        // Server-side: use accept-language header if provided, otherwise default
        const headerValue = acceptLanguageHeader ?? 'nl,en-US;q=0.7,en;q=0.3'
        browserLocales = String(headerValue).split(';')[0]?.split(',') || []

        const { locale, country } = await getData({ appStore, authStore, defaults, browserLocales })
        return `${locale}-${country}`
      } else if (import.meta.client && navigator.languages) {
        // Client-side: use navigator.languages
        browserLocales = navigator.languages
        const { locale, country } = await getData({ appStore, authStore, defaults, browserLocales })
        return `${locale}-${country}`
      } else {
        // Fallback to defaults
        return `${defaults.locale}-${defaults.country}`
      }
    }
  }

  // Step 3: Validate locale-country parameter
  if (!isValidLocaleCountryParam({ appStore, localeCountryParam })) {
    // Step 4: Handle invalid locale-country parameter
    // Special case - only country provided (e.g., /fr, /gb)
    if (hasOnlyValidCountryParam({ appStore, localeCountryParam })) {
      // Return cookieLocale-country (or default locale if no cookie)
      const locale = (cookieLocale as string) || defaults.locale
      return `${locale}-${localeCountryParam}`
    }

    // Otherwise, completely invalid - return cookie-based locale-country
    if (cookieLocale && cookieCountry) {
      return `${cookieLocale}-${cookieCountry}`
    }
    // Fallback to defaults if no cookies
    return `${defaults.locale}-${defaults.country}`
  }

  // Step 5: Handle authenticated user country override
  // Valid locale-country URL - extract locale and country
  const [locale, country] = String(localeCountryParam).split('-')

  if (authStore.isAuthenticated && country !== authStore.user?.user?.country?.name?.toLowerCase()) {
    // User's country doesn't match URL - return locale-userCountry
    const userCountry = authStore.user?.user?.country?.name?.toLowerCase() || defaults.country
    return `${locale}-${userCountry}`
  }

  // Step 6: Return the valid locale-country prefix
  return `${locale || defaults.locale}-${country || defaults.country}`
}
